import { inject } from '@angular/core'

import {
  auditTime,
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs'

import { TRIGGERED_ALERT_NOTIFICATIONS } from '@ti/contracts/data/notifications'
import { RealtimeEventType, TriggeredAlertsListUpdatedData } from '@ti/contracts/data/realtime'
import { Action, Resource } from '@ti/contracts/models/role'
import { ApiService } from '@ti/core/api'
import { Memoize } from '@ti/core/common/decorators'
import { AccessControl } from '@ti/core/common/services/access-control'
import { completeStore } from '@ti/core/common/utils/complete-store'
import { injectDestroy$ } from '@ti/core/common/utils/inject-destroy-event'
import { injectIsPageInForeground$ } from '@ti/core/common/utils/inject-is-bg-fg'
import { CHAT_FACADE } from '@ti/core/contracts/chat'
import { PUSH_CLIENT } from '@ti/core/contracts/push'
import {
  ISidebarBadgesModel,
  RefreshUnreviewedAlertsCountOptions,
  UpdateReason,
} from '@ti/core/contracts/sidebar'
import { RealtimeClient } from '@ti/core/realtime'

interface UnreviewedAlertsCountRefreshRequest {
  reason: UpdateReason
  /** Number of alerts the request covers; weighs it against the high-count refresh rule. */
  changes?: number
}

interface BadgeCounterRefreshIntervalRule {
  countGreaterThan: number
  updateInterval: number | null
}

interface BadgeCounterRefreshConfig {
  count$: BehaviorSubject<number>
  loaded$: BehaviorSubject<boolean>
  refresh$: Subject<void>
  updateInterval: number
  highCountIntervalRules?: BadgeCounterRefreshIntervalRule[]
  hasAccess: () => Promise<boolean>
  load: () => Promise<void> | void
}

export class SidebarBadgesModel implements ISidebarBadgesModel {
  protected readonly accessControl = inject(AccessControl)
  protected readonly api = inject(ApiService)
  protected readonly pushes = inject(PUSH_CLIENT)
  protected readonly chatFacade = inject(CHAT_FACADE)
  protected readonly realtimeClient = inject(RealtimeClient)

  protected readonly destroy$ = injectDestroy$()
  protected readonly isPageInForeground$ = injectIsPageInForeground$()

  protected readonly store = {
    unreviewedAlertsCount$: new BehaviorSubject<number>(0),
    newMediaLibraryItemsCount$: new BehaviorSubject<number>(0),
    overdueAndSoonVehicleServicesCount$: new BehaviorSubject<number>(0),
    needsAttentionInspectionReportsCount$: new BehaviorSubject<number>(0),

    unreviewedAlertsCountLoaded$: new BehaviorSubject<boolean>(false),
    newMediaLibraryItemsCountLoaded$: new BehaviorSubject<boolean>(false),
    overdueAndSoonVehicleServicesCountLoaded$: new BehaviorSubject<boolean>(false),
    needsAttentionInspectionReportsCountLoaded$: new BehaviorSubject<boolean>(false),

    refreshUnreviewedAlertCount$: new Subject<UnreviewedAlertsCountRefreshRequest>(),
    refreshNewMediaLibraryItemsCount$: new Subject<void>(),
    refreshOverdueAndSoonVehicleServicesCount$: new Subject<void>(),
    refreshNeedsAttentionInspectionReportsCount$: new Subject<void>(),
  }

  protected readonly COUNTERS_UPDATE_INTERVAL = 3 * 60_000
  protected readonly MEDIA_LIBRARY_ITEMS_HIGH_COUNT_UPDATE_INTERVAL = 15 * 60_000
  protected readonly MEDIA_LIBRARY_ITEMS_VERY_HIGH_COUNT_UPDATE_INTERVAL = 30 * 60_000
  protected readonly OVERDUE_AND_SOON_VEHICLE_SERVICES_UPDATE_INTERVAL = 60 * 60_000
  protected readonly NEEDS_ATTENTION_INSPECTION_REPORTS_UPDATE_INTERVAL = 15 * 60_000
  protected readonly OVERDUE_AND_SOON_VEHICLE_SERVICES_HIGH_COUNT_UPDATE_INTERVAL = 3 * 60 * 60_000
  protected readonly NEEDS_ATTENTION_INSPECTION_REPORTS_HIGH_COUNT_UPDATE_INTERVAL = 30 * 60_000

  protected readonly BADGE_DISPLAY_COUNT_LIMIT = 99
  protected readonly BADGE_SLOW_REFRESH_COUNT_LIMIT = 120
  protected readonly BADGE_STOP_REFRESH_COUNT_LIMIT = 150

  protected readonly UNREVIEWED_ALERTS_HIGH_COUNT_THRESHOLD = 150
  protected readonly UNREVIEWED_ALERTS_INDIVIDUAL_CHANGES_REFRESH_THRESHOLD = 50
  protected readonly UNREVIEWED_ALERTS_INDIVIDUAL_CHANGES_DEBOUNCE = 1000
  protected readonly UNREVIEWED_ALERTS_REALTIME_REFRESH_THROTTLE = 2000

  protected unreviewedAlertsIndividualChangesSinceLastRefresh = 0
  protected isLoadingUnreviewedAlertsCount = false
  protected unreviewedAlertsCountReloadRequested = false
  protected destroyed = false

  public constructor() {
    this.destroy$.subscribe(() => (this.destroyed = true))
  }

  @Memoize()
  public get state() {
    return {
      counters$: this.countersDataStream(),
    }
  }

  public init(): void {
    const pushNotificationsEnabled$ = this.createPushNotificationsEnabledStream()

    this.initUnreviewedAlertsCountUpdates(pushNotificationsEnabled$)

    this.initBadgeCounterUpdates({
      count$: this.store.newMediaLibraryItemsCount$,
      loaded$: this.store.newMediaLibraryItemsCountLoaded$,
      refresh$: this.store.refreshNewMediaLibraryItemsCount$,
      updateInterval: this.COUNTERS_UPDATE_INTERVAL,
      highCountIntervalRules: [
        {
          countGreaterThan: this.BADGE_STOP_REFRESH_COUNT_LIMIT,
          updateInterval: null,
        },
        {
          countGreaterThan: this.BADGE_SLOW_REFRESH_COUNT_LIMIT,
          updateInterval: this.MEDIA_LIBRARY_ITEMS_VERY_HIGH_COUNT_UPDATE_INTERVAL,
        },
        {
          countGreaterThan: this.BADGE_DISPLAY_COUNT_LIMIT,
          updateInterval: this.MEDIA_LIBRARY_ITEMS_HIGH_COUNT_UPDATE_INTERVAL,
        },
      ],
      hasAccess: () => this.accessControl.check(Resource.Media, Action.View),
      load: () => this.loadNewMediaLibraryItemsCount(),
    })

    this.initBadgeCounterUpdates({
      count$: this.store.overdueAndSoonVehicleServicesCount$,
      loaded$: this.store.overdueAndSoonVehicleServicesCountLoaded$,
      refresh$: this.store.refreshOverdueAndSoonVehicleServicesCount$,
      updateInterval: this.OVERDUE_AND_SOON_VEHICLE_SERVICES_UPDATE_INTERVAL,
      highCountIntervalRules: [
        {
          countGreaterThan: this.BADGE_DISPLAY_COUNT_LIMIT,
          updateInterval: this.OVERDUE_AND_SOON_VEHICLE_SERVICES_HIGH_COUNT_UPDATE_INTERVAL,
        },
      ],
      hasAccess: () => this.accessControl.check(Resource.Maintenance, Action.View),
      load: () => this.loadOverdueAndSoonVehicleServicesCount(),
    })

    this.initBadgeCounterUpdates({
      count$: this.store.needsAttentionInspectionReportsCount$,
      loaded$: this.store.needsAttentionInspectionReportsCountLoaded$,
      refresh$: this.store.refreshNeedsAttentionInspectionReportsCount$,
      updateInterval: this.NEEDS_ATTENTION_INSPECTION_REPORTS_UPDATE_INTERVAL,
      highCountIntervalRules: [
        {
          countGreaterThan: this.BADGE_DISPLAY_COUNT_LIMIT,
          updateInterval: this.NEEDS_ATTENTION_INSPECTION_REPORTS_HIGH_COUNT_UPDATE_INTERVAL,
        },
      ],
      hasAccess: () => this.accessControl.check(Resource.Maintenance, Action.View),
      load: () => this.loadNeedsAttentionInspectionReportsCount(),
    })

    this.destroy$.subscribe(() => completeStore(this.store))
  }

  public countersDataStream(): Observable<Record<string, any>> {
    return combineLatest([
      this.store.newMediaLibraryItemsCount$,
      this.store.unreviewedAlertsCount$,
      this.chatFacade.state$.totalUnreadCount,
      this.store.overdueAndSoonVehicleServicesCount$,
      this.store.needsAttentionInspectionReportsCount$,
    ]).pipe(
      takeUntil(this.destroy$),
      map(
        ([
          newMediaLibraryItems,
          unreviewedAlerts,
          chatTotalUnreadCount,
          overdueAndSoonVehicleServicesCount,
          needsAttentionInspectionReportsCount,
        ]) => {
          return {
            newMediaLibraryItems,
            unreviewedAlerts,
            chatTotalUnreadCount,
            overdueAndSoonVehicleServicesCount,
            needsAttentionInspectionReportsCount,
          } as Record<string, any> // temporary fix - TODO: Fix type and template logic
        },
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  public refreshUnreviewedAlertsCount(options: RefreshUnreviewedAlertsCountOptions = {}): void {
    this.store.refreshUnreviewedAlertCount$.next({ reason: options.reason ?? UpdateReason.Manual })
  }

  public refreshNewMediaLibraryItemsCount(): void {
    this.store.refreshNewMediaLibraryItemsCount$.next()
  }

  public refreshOverdueAndSoonVehicleServicesCount(): void {
    this.store.refreshOverdueAndSoonVehicleServicesCount$.next()
  }

  public refreshNeedsAttentionInspectionReportsCount(): void {
    this.store.refreshNeedsAttentionInspectionReportsCount$.next()
  }

  public async loadUnreviewedAlertsCount(): Promise<void> {
    if (this.isLoadingUnreviewedAlertsCount) {
      // The running request may have read the count before the change that asked for this one.
      this.unreviewedAlertsCountReloadRequested = true
      return
    }

    this.isLoadingUnreviewedAlertsCount = true

    try {
      const accessAllowed = await this.accessControl.check(Resource.AlertWorkflow, Action.View)
      if (!accessAllowed) return

      const number = await this.api.fleet.triggeredAlert.unreviewedCount()

      if (number !== null && number !== undefined) {
        this.store.unreviewedAlertsCount$.next(number)
        this.store.unreviewedAlertsCountLoaded$.next(true)
        this.unreviewedAlertsIndividualChangesSinceLastRefresh = 0
      }
    } catch (e) {
      console.error(e)
    } finally {
      this.isLoadingUnreviewedAlertsCount = false
      if (this.unreviewedAlertsCountReloadRequested) {
        this.unreviewedAlertsCountReloadRequested = false
        if (!this.destroyed) {
          void this.loadUnreviewedAlertsCount()
        }
      }
    }
  }

  public async loadNewMediaLibraryItemsCount(): Promise<void> {
    const accessAllowed = await this.accessControl.check(Resource.Media, Action.View)
    if (!accessAllowed) return

    try {
      const number = await this.api.fleet.media.countNew()
      this.store.newMediaLibraryItemsCount$.next(number)
      this.store.newMediaLibraryItemsCountLoaded$.next(true)
    } catch (e) {
      console.error(e)
    }
  }

  public async loadOverdueAndSoonVehicleServicesCount(): Promise<void> {
    const accessAllowed = await this.accessControl.check(Resource.Maintenance, Action.View)
    if (!accessAllowed) return

    try {
      const number = await this.api.fleet.vehicleScheduledService.countOverdueSoonBadge()

      this.store.overdueAndSoonVehicleServicesCount$.next(number)
      this.store.overdueAndSoonVehicleServicesCountLoaded$.next(true)
    } catch (e) {
      console.error(e)
    }
  }

  public async loadNeedsAttentionInspectionReportsCount(): Promise<void> {
    const accessAllowed = await this.accessControl.check(Resource.Maintenance, Action.View)
    if (!accessAllowed) return

    try {
      const number = await firstValueFrom(this.api.fleet.maintenanceReports.countNeedsAttention$())

      this.store.needsAttentionInspectionReportsCount$.next(number)
      this.store.needsAttentionInspectionReportsCountLoaded$.next(true)
    } catch (e) {
      console.error(e)
    }
  }

  protected initBadgeCounterUpdates(config: BadgeCounterRefreshConfig): void {
    void this.initAccessibleBadgeCounterUpdates(config)
  }

  protected async initAccessibleBadgeCounterUpdates(
    config: BadgeCounterRefreshConfig,
  ): Promise<void> {
    const accessAllowed = await config.hasAccess()
    if (!accessAllowed) return

    let isLoading = false

    const load = async (): Promise<void> => {
      if (isLoading) return

      isLoading = true

      try {
        await config.load()
      } finally {
        isLoading = false
      }
    }

    await load()

    merge(
      config.refresh$,
      this.createBadgeCounterTimer$(
        config.count$,
        config.loaded$,
        config.updateInterval,
        config.highCountIntervalRules,
      ),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        void load()
      })
  }

  protected createBadgeCounterTimer$(
    count$: BehaviorSubject<number>,
    loaded$: BehaviorSubject<boolean>,
    updateInterval: number,
    highCountIntervalRules: BadgeCounterRefreshIntervalRule[] = [],
  ): Observable<unknown> {
    return combineLatest([count$, loaded$]).pipe(
      switchMap(([count, isLoaded]) => {
        if (!isLoaded) {
          return EMPTY
        }

        const interval = this.getBadgeCounterRefreshInterval(
          count,
          updateInterval,
          highCountIntervalRules,
        )

        if (!interval) {
          return EMPTY
        }

        return this.createForegroundTimer$(interval, interval)
      }),
    )
  }

  protected async initUnreviewedAlertsCountUpdates(
    pushNotificationsEnabled$: Observable<boolean>,
  ): Promise<void> {
    const accessAllowed = await this.accessControl.check(Resource.AlertWorkflow, Action.View)
    if (!accessAllowed) return

    await this.loadUnreviewedAlertsCount()

    this.createUnreviewedAlertsCountTimer$(pushNotificationsEnabled$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        void this.loadUnreviewedAlertsCount()
      })

    this.store.refreshUnreviewedAlertCount$
      .pipe(
        filter(
          ({ reason }) =>
            reason !== UpdateReason.IndividualStatusChange && reason !== UpdateReason.Realtime,
        ),
        debounceTime(500),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        void this.loadUnreviewedAlertsCount()
      })

    this.store.refreshUnreviewedAlertCount$
      .pipe(
        filter(({ reason }) => reason === UpdateReason.IndividualStatusChange),
        tap(() => this.unreviewedAlertsIndividualChangesSinceLastRefresh++),
        filter(() => this.shouldRefreshAfterIndividualAlertStatusChange()),
        debounceTime(this.UNREVIEWED_ALERTS_INDIVIDUAL_CHANGES_DEBOUNCE),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        void this.loadUnreviewedAlertsCount()
      })

    // auditTime, not debounceTime: a steady stream of remote changes must not starve the badge.
    this.store.refreshUnreviewedAlertCount$
      .pipe(
        filter(({ reason }) => reason === UpdateReason.Realtime),
        tap(
          ({ changes }) => (this.unreviewedAlertsIndividualChangesSinceLastRefresh += changes ?? 1),
        ),
        filter(() => this.shouldRefreshAfterIndividualAlertStatusChange()),
        auditTime(this.UNREVIEWED_ALERTS_REALTIME_REFRESH_THROTTLE),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        void this.loadUnreviewedAlertsCount()
      })

    this.pushes.messages$.pipe(takeUntil(this.destroy$)).subscribe((msg) => {
      if (msg?.type !== undefined && TRIGGERED_ALERT_NOTIFICATIONS.includes(msg.type)) {
        this.store.refreshUnreviewedAlertCount$.next({ reason: UpdateReason.Push })
      }
    })

    this.initUnreviewedAlertsRealtimeUpdates()
  }

  /** Subscribed only after the access check, so realtime never starts an unauthorized load. */
  protected initUnreviewedAlertsRealtimeUpdates(): void {
    if (this.destroyed) return

    this.realtimeClient
      .subscribeToEvents(RealtimeEventType.TriggeredAlertsListUpdated, (event) => {
        const data = event.data as TriggeredAlertsListUpdatedData | undefined
        // Driver actions never change Unreviewed membership.
        if (data?.unreviewedCountChanged === false) return

        const ids = data?.triggeredAlertIds
        this.store.refreshUnreviewedAlertCount$.next({
          reason: UpdateReason.Realtime,
          changes: Array.isArray(ids) && ids.length ? ids.length : 1,
        })
      })
      .then((unsubscribe) => {
        // A model destroyed before this settles has already emitted destroy$.
        if (this.destroyed) {
          unsubscribe()
        } else {
          this.destroy$.subscribe(() => unsubscribe())
        }
      })
      .catch((error) => console.error('Cannot subscribe to triggered alert updates', error))
  }

  protected createPushNotificationsEnabledStream(): Observable<boolean> {
    return merge(
      from(this.pushes.isRegistered()).pipe(
        catchError((e) => {
          console.warn('Cannot determine push registration status', e)
          return of(false)
        }),
      ),
      this.pushes.isRegistered$,
    ).pipe(startWith(false), distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: true }))
  }

  protected createUnreviewedAlertsCountTimer$(
    pushNotificationsEnabled$: Observable<boolean>,
  ): Observable<unknown> {
    return combineLatest([
      this.store.unreviewedAlertsCount$,
      this.store.unreviewedAlertsCountLoaded$,
      pushNotificationsEnabled$,
    ]).pipe(
      switchMap(([count, isLoaded, pushNotificationsEnabled]) => {
        if (!isLoaded || pushNotificationsEnabled) {
          return EMPTY
        }

        const interval = this.getUnreviewedAlertsCountRefreshInterval(count)
        if (!interval) {
          return EMPTY
        }

        return this.createForegroundTimer$(interval, interval)
      }),
    )
  }

  protected createForegroundTimer$(
    initialDelay: number,
    updateInterval: number,
  ): Observable<unknown> {
    let timerLastEvent: number | undefined

    return combineLatest([timer(initialDelay, updateInterval), this.isPageInForeground$]).pipe(
      filter(([, isPageInForeground]) => {
        if (!isPageInForeground) return false
        return !timerLastEvent || timerLastEvent <= Date.now() - updateInterval
      }),
      tap(() => (timerLastEvent = Date.now())),
    )
  }

  protected getUnreviewedAlertsCountRefreshInterval(count: number): number | null {
    if (count <= 50) return 3 * 60_000
    if (count <= 75) return 4 * 60_000
    if (count <= 100) return 5 * 60_000
    if (count <= 120) return 10 * 60_000
    if (count <= this.UNREVIEWED_ALERTS_HIGH_COUNT_THRESHOLD) return 15 * 60_000
    return null
  }

  protected getBadgeCounterRefreshInterval(
    count: number,
    updateInterval: number,
    highCountIntervalRules: BadgeCounterRefreshIntervalRule[],
  ): number | null {
    for (const rule of highCountIntervalRules) {
      if (count > rule.countGreaterThan) {
        return rule.updateInterval
      }
    }

    return updateInterval
  }

  protected shouldRefreshAfterIndividualAlertStatusChange(): boolean {
    const currentCount = this.store.unreviewedAlertsCount$.getValue()

    if (currentCount > this.UNREVIEWED_ALERTS_HIGH_COUNT_THRESHOLD) {
      return (
        this.unreviewedAlertsIndividualChangesSinceLastRefresh >=
        this.UNREVIEWED_ALERTS_INDIVIDUAL_CHANGES_REFRESH_THRESHOLD
      )
    }

    return true
  }
}
